import { useRef, useState, useEffect, useCallback } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Download, ExternalLink, Megaphone, QrCode, Mail, Eye, Loader2, History, Send, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { downloadCanvasPng } from "@/components/mc/marketingUtils";
import { opApi, apiError } from "@/lib/api";
import { MorrLink } from "@/components/mc/MorrLink";

function QrCard({ title, description, url, filename, accent }) {
  const ref = useRef(null);
  return (
    <div className="border border-border bg-card p-5 flex flex-col" data-testid={`marketing-qr-${filename}`}>
      <h3 className="font-display font-bold text-base">{title}</h3>
      <p className="text-xs text-muted-foreground mt-1 mb-4 flex-1">{description}</p>
      <div className="flex items-start gap-4">
        <div className="bg-white p-2.5 border border-border shrink-0" ref={ref}>
          <QRCodeCanvas value={url} size={128} level="M" fgColor={accent || "#111111"} />
        </div>
        <div className="min-w-0 flex-1">
          <a href={url} target="_blank" rel="noreferrer"
            className="text-xs font-mono text-primary break-all hover:underline flex items-start gap-1"
            data-testid={`marketing-link-${filename}`}>
            {url}<ExternalLink className="h-3 w-3 mt-0.5 shrink-0" />
          </a>
          <Button variant="outline" size="sm" className="rounded-none gap-1.5 mt-3"
            onClick={() => downloadCanvasPng(ref.current, filename)} data-testid={`marketing-download-${filename}`}>
            <Download className="h-3.5 w-3.5" /> Download PNG
          </Button>
        </div>
      </div>
    </div>
  );
}

const BASE_CATEGORY_ORDER = ["Orders", "Account & login", "Events & catering"];

function SendTestPopover({ p, onSend, sending }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const busy = sending === p.key;
  const submit = () => {
    const to = email.trim();
    if (to && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to)) { toast.error("Please enter a valid email address."); return; }
    onSend(p, to);
    setOpen(false);
    setEmail("");
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="rounded-none gap-1.5 shrink-0"
          disabled={busy} data-testid={`email-test-btn-${p.key}`}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send test
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 rounded-none" data-testid={`email-test-popover-${p.key}`}>
        <label className="text-xs font-medium">Send this test to</label>
        <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email"
          placeholder="your login email" className="rounded-none mt-1.5 h-9"
          onKeyDown={(e) => e.key === "Enter" && submit()}
          data-testid={`email-test-input-${p.key}`} />
        <p className="text-[11px] text-muted-foreground mt-1.5 leading-snug">Leave blank to send to your own login email.</p>
        <Button size="sm" className="rounded-none w-full mt-3 gap-1.5" onClick={submit}
          disabled={busy} data-testid={`email-test-send-${p.key}`}>
          <Send className="h-3.5 w-3.5" /> Send test
        </Button>
      </PopoverContent>
    </Popover>
  );
}

function EmailAutomations() {
  const [previews, setPreviews] = useState([]);
  const [live, setLive] = useState(false);
  const [sender, setSender] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [active, setActive] = useState(null);
  const [sending, setSending] = useState("");

  const sendTest = (p, email) => {
    setSending(p.key);
    opApi.post(`/tenant/email-previews/${p.key}/test`, email ? { to: email } : {})
      .then((r) => {
        const to = r.data?.to || "your inbox";
        toast.success(`Test "${p.subject}" sent to ${to} through Morr Notify`);
      })
      .catch((e) => toast.error(apiError(e.response?.data?.detail) || "Could not send the test email."))
      .finally(() => setSending(""));
  };

  useEffect(() => {
    let cancelled = false;
    opApi.get("/tenant/email-previews")
      .then((r) => {
        if (cancelled) return;
        setPreviews(r.data.previews || []);
        setLive(!!r.data.live);
        setSender(r.data.sender || "");
      })
      .catch((e) => !cancelled && setError(apiError(e.response?.data?.detail)))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return <div className="flex items-center gap-2 text-sm text-muted-foreground py-6" data-testid="emails-loading">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading email previews…
    </div>;
  }
  if (error) return <p className="text-sm text-destructive py-4" data-testid="emails-error">{error}</p>;

  const CATEGORY_ORDER = [...BASE_CATEGORY_ORDER, ...[...new Set(previews.map((p) => p.category))].filter((c) => !BASE_CATEGORY_ORDER.includes(c))];
  const groups = CATEGORY_ORDER
    .map((cat) => ({ cat, rows: previews.filter((p) => p.category === cat) }))
    .filter((g) => g.rows.length);

  return (
    <div data-testid="email-automations">
      <div className="flex items-start gap-2 mb-5 text-xs text-muted-foreground">
        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 border ${live ? "border-emerald-300 text-emerald-700 bg-emerald-50" : "border-amber-300 text-amber-700 bg-amber-50"}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${live ? "bg-emerald-500" : "bg-amber-500"}`} />
          {live ? "Sending through Morr Notify" : "Not sending: shop isn't linked to Morr"}
        </span>
        <p className="max-w-xl">{live
          ? <>These emails are sent automatically on your behalf by Morr Notify{sender ? <> from <strong>{sender}</strong></> : null}, with your business name, logo and contact details. Replies go to your verified contact email in your Morr business profile.</>
          : <>Once this shop is linked to Morr, these emails go out automatically on your behalf through Morr Notify.</>} Click <strong>Preview</strong> to see exactly what your customers receive.</p>
      </div>
      {!live && (
        <div className="border border-amber-300 bg-amber-50 text-amber-800 text-sm px-4 py-3 mb-5" data-testid="emails-not-linked-note">
          This shop isn't linked to a Morr business yet, so automated emails aren't being sent. Ask your Morr Access admin to link it. Each skipped email still shows in Sent history with the reason.
        </div>
      )}

      <div className="space-y-6">
        {groups.map((g) => (
          <div key={g.cat} data-testid={`email-group-${g.cat.replace(/\W+/g, "-").toLowerCase()}`}>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">{g.cat}</h4>
            <div className="border border-border divide-y divide-border">
              {g.rows.map((p) => (
                <div key={p.key} className="flex items-center gap-4 px-4 py-3" data-testid={`email-row-${p.key}`}>
                  <Mail className="h-4 w-4 text-primary shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{p.subject}</div>
                    <div className="text-xs text-muted-foreground">{p.trigger}</div>
                  </div>
                  <Button variant="outline" size="sm" className="rounded-none gap-1.5 shrink-0"
                    onClick={() => setActive(p)} data-testid={`email-preview-btn-${p.key}`}>
                    <Eye className="h-3.5 w-3.5" /> Preview
                  </Button>
                  <SendTestPopover p={p} onSend={sendTest} sending={sending} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-2xl p-0 gap-0" data-testid="email-preview-dialog">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-border">
            <DialogTitle className="text-base">{active?.subject}</DialogTitle>
            <DialogDescription className="text-xs">{active?.trigger}</DialogDescription>
          </DialogHeader>
          <div className="bg-[#f5f5f5] max-h-[70vh] overflow-auto">
            {active && (
              <iframe title="email-preview" srcDoc={active.html}
                className="w-full border-0" style={{ height: "60vh" }}
                sandbox="" data-testid="email-preview-frame" />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatusBadge({ status }) {
  if (status === "sent")
    return <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Sent</span>;
  if (status === "logged")
    return <span className="inline-flex items-center gap-1 text-xs text-amber-700"><Mail className="h-3.5 w-3.5" /> Test log</span>;
  if (status === "skipped" || status === "blocked")
    return <span className="inline-flex items-center gap-1 text-xs text-amber-700"><XCircle className="h-3.5 w-3.5" /> Not sent</span>;
  return <span className="inline-flex items-center gap-1 text-xs text-destructive"><XCircle className="h-3.5 w-3.5" /> Failed</span>;
}

function SentHistory() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [active, setActive] = useState(null);
  const [resending, setResending] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    opApi.get("/tenant/email-log")
      .then((r) => setRows(r.data || []))
      .catch((e) => setError(apiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openPreview = (id) => {
    setActive({ loading: true });
    opApi.get(`/tenant/email-log/${id}`)
      .then((r) => setActive(r.data))
      .catch((e) => { toast.error(apiError(e.response?.data?.detail)); setActive(null); });
  };

  const resend = (id) => {
    setResending(id);
    opApi.post(`/tenant/email-log/${id}/resend`)
      .then((r) => { toast.success(r.data.message || "Re-sent."); load(); })
      .catch((e) => toast.error(apiError(e.response?.data?.detail)))
      .finally(() => setResending(null));
  };

  const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

  if (loading) return <div className="flex items-center gap-2 text-sm text-muted-foreground py-6" data-testid="history-loading"><Loader2 className="h-4 w-4 animate-spin" /> Loading sent history…</div>;
  if (error) return <p className="text-sm text-destructive py-4" data-testid="history-error">{error}</p>;

  return (
    <div data-testid="sent-history">
      <MorrLink to="emails" label="View email log in Morr Access" testId="history-morr-link" className="mb-3" />
      <p className="text-xs text-muted-foreground mb-4 max-w-2xl">
        Every automated email sent on your behalf. Re-send informational emails (order receipts, event confirmations) if a customer lost theirs. Secure sign-in / reset links can't be re-sent.
      </p>
      {rows.length === 0 ? (
        <div className="border border-dashed border-border py-10 text-center text-sm text-muted-foreground" data-testid="history-empty">
          No emails sent yet. Once customers place orders or you send event updates, they'll show up here.
        </div>
      ) : (
        <div className="border border-border divide-y divide-border" data-testid="history-list">
          {rows.map((r) => (
            <div key={r.id} className="flex items-center gap-4 px-4 py-3" data-testid={`history-row-${r.id}`}>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{r.subject}</div>
                <div className="text-xs text-muted-foreground truncate">
                  {r.to_address} · {fmt(r.created_at)}{r.resend_of && !String(r.resend_of).startsWith("test-") ? " · re-sent" : ""}
                </div>
                {r.status !== "sent" && r.error && (
                  <div className="text-xs text-amber-700 truncate" data-testid={`history-reason-${r.id}`}>{r.error.replace(/^not sent: /, "Not sent: ")}</div>
                )}
              </div>
              <StatusBadge status={r.status} />
              {r.delivery_status && (
                <span className={`text-xs capitalize ${/bounce|complain|fail|reject/.test(r.delivery_status) ? "text-red-700" : "text-emerald-700"}`} data-testid={`history-delivery-${r.id}`}>{r.delivery_status}</span>
              )}
              <Button variant="outline" size="sm" className="rounded-none gap-1.5 shrink-0"
                onClick={() => openPreview(r.id)} data-testid={`history-view-${r.id}`}>
                <Eye className="h-3.5 w-3.5" /> View
              </Button>
              <Button variant="outline" size="sm" className="rounded-none gap-1.5 shrink-0"
                disabled={!r.resendable || !r.notify_template || r.status === "skipped" || /bounce|complain|spam/i.test(r.delivery_status || "") || resending === r.id}
                title={r.resendable ? "Re-send this email" : "Contains a one-time secure link — can't be re-sent"}
                onClick={() => resend(r.id)} data-testid={`history-resend-${r.id}`}>
                {resending === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Resend
              </Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-2xl p-0 gap-0" data-testid="history-preview-dialog">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-border">
            <DialogTitle className="text-base">{active?.subject || "Loading…"}</DialogTitle>
            <DialogDescription className="text-xs">{active?.to_address}</DialogDescription>
          </DialogHeader>
          <div className="bg-[#f5f5f5] max-h-[70vh] overflow-auto">
            {active?.html && (
              <iframe title="sent-email-preview" srcDoc={active.html}
                className="w-full border-0" style={{ height: "60vh" }}
                sandbox="" data-testid="history-preview-frame" />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function MarketingTab({ slug, name, accent }) {
  const origin = window.location.origin;
  const cards = [
    { title: "Menu page", filename: "menu", description: "Your standalone MorrCart page — opens your full menu/catalog so customers can browse and order.", url: `${origin}/preview/${slug}` },
    { title: "Join & earn rewards", filename: "join", description: "One tap to sign up: creates the customer's account and enrolls them in your rewards. This is how customers join your loyalty program.", url: `${origin}/join/${slug}` },
  ];
  return (
    <div data-testid="marketing-tab">
      <div className="flex items-center gap-3 mb-1">
        <Megaphone className="h-5 w-5 text-primary" />
        <h2 className="font-display font-bold text-xl">Marketing</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-6 max-w-2xl">
        Share your store and see everything MorrCart sends to your customers — all in one place.
      </p>

      <Accordion type="multiple" defaultValue={["qr"]} className="border border-border">
        <AccordionItem value="qr" className="px-6">
          <AccordionTrigger className="text-sm font-semibold uppercase tracking-wider" data-testid="marketing-section-qr">
            <span className="flex items-center gap-2"><QrCode className="h-4 w-4" /> QR codes</span>
          </AccordionTrigger>
          <AccordionContent className="pb-6">
            <p className="text-xs text-muted-foreground mb-4 max-w-2xl">
              Printable QR codes that link customers to your key pages. Add them to flyers, table tents, receipts
              or your storefront window. Per-offer loyalty QR codes live on each program in the Loyalty tab.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {cards.map((c) => <QrCard key={c.filename} {...c} accent={accent} />)}
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="emails" className="px-6">
          <AccordionTrigger className="text-sm font-semibold uppercase tracking-wider" data-testid="marketing-section-emails">
            <span className="flex items-center gap-2"><Mail className="h-4 w-4" /> Automated emails</span>
          </AccordionTrigger>
          <AccordionContent className="pb-6">
            <EmailAutomations />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="history" className="px-6 border-b-0">
          <AccordionTrigger className="text-sm font-semibold uppercase tracking-wider" data-testid="marketing-section-history">
            <span className="flex items-center gap-2"><History className="h-4 w-4" /> Sent history</span>
          </AccordionTrigger>
          <AccordionContent className="pb-6">
            <SentHistory />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
